import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//Common
import { LoadingIndicator } from "../../common/VFX-UI";

//App
import Claim from "./Claim";
import claimApi from "../../../app/model/api/claimApi";
import lookupValueApi from "../../../app/model/api/lookupValueApi";
import { queryKeys, CLAIMTYPE_ID } from "./constants";
import { queryKeys as lookupValueQueryKeys } from "../Lookup/LookupValue/constants";
import { FormikTypeaheadOption, SortBy } from "../../../types";
import { ClaimContextData, ClaimState } from "../../../types/claim";
import {
  EMPTY_GUID,
  GRID_PAGE_SIZE,
} from "../../../utility/constants/constants";
import {
  selectIsActive,
  setIsActive,
  selectSearchValue,
  setSearchValue,
} from "../../../store/claim";
import { SearchValue } from "../../../store/types";

export const ClaimContext = React.createContext<ClaimContextData | null>(null);

const ClaimContainer = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState<ClaimState>({
    claimTypeId: null,
    claimId: null,
    activeToggle: useSelector(selectIsActive) ?? true,
    searchPayload: useSelector(selectSearchValue) ?? null,
    pageSize: GRID_PAGE_SIZE,
    currentPage: 1,
    sortBy: [{ field: "name", direction: 1 }],
  });

  const statesQuery = useQuery({
    queryKey: [lookupValueQueryKeys.getLookupValues],
    queryFn: () =>
      lookupValueApi.getAllByLookupTypeId({
        lookupTypeId: CLAIMTYPE_ID,
        isActive: true,
      }),
    select: (data) => {
      const transformedData = data?.result?.map((claimType) => ({
        label: claimType.name as string,
        value: claimType.id as string,
      }));
      return transformedData as FormikTypeaheadOption[];
    },
  });

  const claimsGridQuery = useQuery({
    queryKey: [queryKeys.getClaims, state.claimTypeId],
    queryFn: () =>
      claimApi.searchClaims({
        ...state.searchPayload,
        isActive: state.activeToggle,
        typeId: state.claimTypeId ?? EMPTY_GUID,
        pageSize: state.pageSize,
        pageNumber: state.currentPage,
        sortBy: state.sortBy,
      }),
  });

  const claimQuery = useQuery({
    queryKey: [queryKeys.getClaim, state.claimId],
    queryFn: () => claimApi.get(state.claimId ?? EMPTY_GUID),
    enabled: state.claimId !== null,
  });

  const updateClaimId = async (claimId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      claimId: claimId,
    }));
  };

  const updateClaimTypeId = async (claimTypeId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      searchPayload: null,
      claimTypeId: claimTypeId,
    }));
  };

  const { mutate: deleteMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await claimApi.delete(value);
    },
  });

  const { mutate: activateMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await claimApi.activate(value);
    },
  });

  const updateActiveToggle = async () => {
    dispatch(setIsActive());
    setState((prevState) => ({
      ...prevState,
      activeToggle: !prevState.activeToggle,
    }));
  };

  const updateSortBy = async (sortBy: SortBy) => {
    const { field, direction } = sortBy;
    setState((prevState) => ({
      ...prevState,
      sortBy: [{ field, direction }],
    }));
  };

  const updateSearchPayload = async (values: SearchValue | null) => {
    if (values?.name) {
      dispatch(setSearchValue({ name: values.name }));
    }
    if (values && Object.keys(values).length) {
      setState((prevState) => ({
        ...prevState,
        searchPayload: values,
        currentPage: 1,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        searchPayload: null,
        currentPage: 1,
        sortBy: [{ field: "name", direction: 1 }],
      }));
    }
  };

  const updateCurrentPage = async (currentPage: number) => {
    setState((prevState) => ({
      ...prevState,
      currentPage: currentPage,
    }));
  };

  const updateCurrentPageSize = async (pageSize: number) => {
    setState((prevState) => ({
      ...prevState,
      pageSize: pageSize,
    }));
  };

  const contextValue: ClaimContextData = {
    data: claimQuery?.data?.result,
    id: state.claimId,
    setId: updateClaimId,
    claimTypes: statesQuery?.data,
    claimTypeId: state.claimTypeId,
    setClaimTypeId: updateClaimTypeId,
    activeToggle: state.activeToggle,
    setActiveToggleValue: updateActiveToggle,
    searchValue: state.searchPayload ?? undefined,
    setSearchValue: updateSearchPayload,
    setCurrentPage: updateCurrentPage,
    setCurrentPageSize: updateCurrentPageSize,
    setSortBy: updateSortBy,
    paging: {
      currentPage: state.currentPage,
      totalSize: claimsGridQuery?.data?.totalSize ?? 0,
    },
    pageSize: state.pageSize,
    refetchGrid: () => claimsGridQuery?.refetch(),
  };

  if (statesQuery?.isLoading) return <LoadingIndicator />;

  return (
    <ClaimContext.Provider value={contextValue}>
      <Claim
        data={claimsGridQuery?.data?.result}
        deleteMutation={deleteMutation}
        activateMutation={activateMutation}
      />
    </ClaimContext.Provider>
  );
};

export default ClaimContainer;
